import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import { push } from 'connected-react-router'
import { Spacer } from 'components/Utility'
import { SmallHeadline } from 'components/Typography'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { SearchForm } from 'components/Form'
import { useSelectCallback, useStringChangeEvent } from 'hooks/form'
import { useSkip } from 'hooks/effect'
import { FilterLabel, DeliveryOrderContainer, OrderStatusLabels } from 'components/Order'
import { StoreShippingType } from 'types/storeShipping'
import { OrderTime, OrderType } from 'types/checkout'
import { fetchStoreShippings, initStoreShippings, unsubscribeStoreShippings } from 'reducks/storeShipping/operations'
import { countOrderTimes, fetchOrders, initOrders, unsubscribeOrders } from 'reducks/checkout/operations'
import { getStoreShippings } from 'reducks/storeShipping/selectors'
import { getOrders, getOrderTimes } from 'reducks/checkout/selectors'
import { BlueButton, NavButton, ScrollTopButton } from 'components/Button'
import { getStoreRef } from 'reducks/auth/selectors'
import { fetchBreads } from 'reducks/bread/operations'
import { FlexRow } from 'components/Flex'
import { useSumDeliveryOrderAmount } from 'hooks/order'
import { StockUnionType } from 'types/stock'
import { fetchUsers } from 'reducks/user/operations'
import { getUsers } from 'reducks/user/selectors'

const statuses = ['準備が必要', '準備済', '発送済', 'すべて']
const orderTypes = ['delivery'] as StockUnionType[]

type Props = {
  currentDate: Date
  date: string | undefined
  isFuture: boolean
  isToday: boolean
}

const DeliveryOrderTemplate: FC<Props> = (props) => {
  const { currentDate, date, isFuture, isToday } = props

  const dispatch = useDispatch()
  const orderTimes: OrderTime[] = useSelector((state: State) => getOrderTimes(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const storeShippings: StoreShippingType[] = useSelector((state: State) => getStoreShippings(state))
  const storeOrders = useSelector((state: State) => getOrders(state))
  const users = useSelector((state: State) => getUsers(state))
  const storeRefs = useMemo(() => [storeRef], [storeRef])

  const [keyword, setKeyword] = useState<string>('')
  const [storeShippingList, setStoreShippingList] = useState<StoreShippingType[]>([])
  const [orders, setOrders] = useState<OrderType[]>([])
  // 注文の表示デフォルト...当日・未来「準備が必要」, 過去「すべて」
  const [status, setStatus] = useState<string>(isFuture || isToday ? '準備が必要' : 'すべて')

  const handleKeyword = useStringChangeEvent(setKeyword)
  const selectStatus = useSelectCallback(setStatus)
  // 注文番号に対応するユーザー情報を取得する
  const userIds = useMemo(() => storeShippingList.map((storeShipping) => storeShipping.user_id), [storeShippingList])
  // 現在表示中の注文件数
  const orderAmount = useSumDeliveryOrderAmount(storeShippingList, orders)

  // フォームに入力された値を部分一致で検索
  const searchKeyword = useCallback(
    (keyword: string) => {
      const storeShippingList = storeShippings.filter((storeShipping) => {
        const regexp = new RegExp('^(?=.*' + keyword + ').*$', 'g')
        const user = users.find((user) => user.user_id === storeShipping.user_id)
        // 注文番号か注文者名で部分一致
        return regexp.test(storeShipping.shipping_number) || regexp.test(user ? user.name : '')
      })
      setStoreShippingList([...storeShippingList])
    },
    [storeShippings, setStoreShippingList, users]
  )

  // 日付が変わったら注文番号を取得する
  useEffect(() => {
    if (!date || !storeRef) return
    dispatch(initStoreShippings())
    dispatch(fetchStoreShippings(date, storeRef))
    return () => {
      dispatch(unsubscribeStoreShippings())
    }
  }, [date, storeRef])

  // パン情報を取得
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchBreads(storeRef, 'all', 'name'))
  }, [storeRef])

  // 注文回数を取得
  useEffect(() => {
    if (!currentDate || !storeRefs || !userIds || storeRefs.length === 0 || userIds.length === 0) return
    dispatch(countOrderTimes(userIds, currentDate, storeRefs))
  }, [userIds, currentDate, storeRefs])

  // 顧客情報を取得
  useEffect(() => {
    if (!userIds || userIds.length === 0) return
    dispatch(fetchUsers(userIds))
  }, [userIds])

  // ステータスまたは注文タイプが変更されたら注文情報を再取得する
  useEffect(() => {
    if (!date || !storeRef) return
    dispatch(initOrders())
    dispatch(
      fetchOrders(
        date,
        status,
        orderTypes,
        status === 'すべて' ? ['succeeded', 'requires_capture', 'canceled'] : ['succeeded', 'requires_capture'],
        storeRef
      )
    )
    return () => {
      dispatch(unsubscribeOrders())
    }
  }, [date, orderTypes, status, storeRef])

  // FirestoreのstoreShippingListとordersを取得→Redux Storeの更新が完了したら、local stateに反映する
  useEffect(() => {
    setKeyword('')
    setStoreShippingList([...storeShippings])
    setOrders([...storeOrders])
  }, [storeShippings, storeOrders])

  // 検索フォームが空になったら検索結果をリセット(初回のレンダリングはスキップ)
  useSkip(
    () => {
      if (keyword !== '') return
      setStoreShippingList([...storeShippings])
    },
    [keyword, storeShippings],
    1
  )

  const isButtonDisabled = storeShippingList.length === 0 || orders.length === 0

  return (
    <div className={styles.standard}>
      <Spacer size={20} />
      <FlexRow justifyContent={'end'}>
        <BlueButton
          disabled={isButtonDisabled}
          label={'印刷する'}
          onClick={() => {
            dispatch(push(`/order/delivery/print/${date}?status=${status}`))
          }}
          size={'xsmall'}
          type={'main'}
        />
        <Spacer size={10} />
        <BlueButton
          disabled={isButtonDisabled}
          label={'送り状CSV出力'}
          onClick={() => {
            dispatch(push(`/order/delivery/export/${date}?status=${status}`))
          }}
          size={'xsmall'}
          type={'main'}
        />
      </FlexRow>
      <Spacer size={20} />
      <SearchForm
        isFilled={keyword !== ''}
        onChange={handleKeyword}
        onClick={() => searchKeyword(keyword)}
        placeholder={'注文番号や注文者で検索'}
        value={keyword}
      />
      <Spacer size={16} />
      <FilterLabel label={'ステータス：'} />
      <Spacer size={8} />
      <OrderStatusLabels selectStatus={selectStatus} status={status} statuses={statuses} />
      <Spacer size={28} />
      <SmallHeadline label={`${orderAmount}件の注文を表示中`} />
      <Spacer size={12} />
      <DeliveryOrderContainer
        orders={orders}
        orderTimes={orderTimes}
        sortType={'checkout_at'}
        storeShippingList={storeShippingList}
        users={users}
      />
      <Spacer size={48} />
      <NavButton label={'トップページに戻る'} iconPath={'/icon/grey/home.svg'} url={'/'} />
      <Spacer size={72} />
      <ScrollTopButton />
      <Spacer size={20} />
    </div>
  )
}

export default DeliveryOrderTemplate
